import { motion, AnimatePresence } from 'framer-motion'
import { X, Package, Trash2 } from 'lucide-react'
import { DECORATIONS, CATEGORY_LABELS, type Decoration } from '@/data/decorations'

interface DecorationInventoryProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  purchasedDecorations: Set<string>
  placedDecorations: { decorationId: string; x: number; y: number; rotation: number }[]
  onSelectDecoration: (decoration: Decoration) => void
  onRemoveDecoration: (index: number) => void
}

export function DecorationInventory({
  open,
  onOpenChange,
  purchasedDecorations,
  placedDecorations,
  onSelectDecoration,
  onRemoveDecoration,
}: DecorationInventoryProps) {
  const purchased = DECORATIONS.filter((d) => purchasedDecorations.has(d.id))
  const placed = placedDecorations.map((p) => ({
    ...p,
    decoration: DECORATIONS.find((d) => d.id === p.decorationId),
  }))

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            className="fixed top-4 left-4 right-4 bottom-4 z-50 max-w-md mx-auto bg-white/90 backdrop-blur-xl rounded-3xl border border-white/80 shadow-[0_25px_80px_rgba(70,140,220,0.35)] flex flex-col overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-lg">
                  <Package className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-black tracking-[0.1em] text-[#2f5d8a]">装饰仓库</h2>
                  <p className="text-xs text-slate-400">选择已购买的装饰放置到鱼缸</p>
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="w-9 h-9 rounded-full bg-white/60 border border-white/80 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {placed.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-500 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    已放置 ({placed.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {placed.map((item, index) => (
                      <motion.button
                        key={`placed-${index}`}
                        className="p-3 rounded-xl bg-green-50/50 border border-green-200 flex items-center gap-3 hover:bg-green-100/50 transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onRemoveDecoration(index)}
                      >
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-3xl">
                          {item.decoration?.emoji}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-bold text-[#2f5d8a] text-sm">{item.decoration?.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {CATEGORY_LABELS[item.decoration?.category || 'prop']}
                          </div>
                        </div>
                        <div className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200">
                          <Trash2 size={14} />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-bold text-slate-500 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  仓库 ({purchased.length})
                </h3>
                {purchased.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="mx-auto mb-3 opacity-40" size={32} />
                    <p className="text-sm">还没有购买任何装饰</p>
                    <p className="text-xs mt-1">去商店购买一些吧！</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {purchased.map((decoration) => (
                      <motion.button
                        key={decoration.id}
                        className="p-3 rounded-xl bg-white/70 border border-white/80 flex items-center gap-3 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100/50 transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          onSelectDecoration(decoration)
                          onOpenChange(false)
                        }}
                      >
                        <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center text-3xl">
                          {decoration.emoji}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-bold text-[#2f5d8a] text-sm">{decoration.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {CATEGORY_LABELS[decoration.category]}
                          </div>
                          {decoration.effect.moodBonus || decoration.effect.energyBonus ? (
                            <div className="text-xs text-green-500 mt-1">
                              {decoration.effect.moodBonus && `心情 +${decoration.effect.moodBonus}`}
                              {decoration.effect.energyBonus && `活力 +${decoration.effect.energyBonus}`}
                            </div>
                          ) : null}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}